import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageLayout } from "@/components/PageLayout";
import { Overline } from "@/components/Reveal";
import { MountReveal, CtaLink } from "@/components/pageKit";
import { PortableText } from "@/components/PortableText";
import NotFoundPage from "@/pages/NotFoundPage";
import { useSeo } from "@/lib/useSeo";
import { peek } from "@/lib/sanity";
import { getPerspectivePage, getPerspectivePosts, getPerspectiveTopics } from "@/lib/sanityQueries";
import { formatDate, readingTime, firstSharedLine, imageUrl, postPath, hasPage, isLive } from "@/lib/perspective";

const FORMATS = [
    { value: "all", label: "All" },
    { value: "article", label: "Articles" },
    { value: "fieldNote", label: "Field Notes" },
];

// Field Notes shown per step of "Show more". 12 fills complete rows in both the
// two- and three-column layouts; phones get 6 because notes stack in one column.
const NOTES_PAGE_SIZE = 12;
const NOTES_PAGE_SIZE_PHONE = 6;
const PHONE_QUERY = "(max-width: 767px)";

// Starts at the desktop size so prerendered HTML and hydration match, then
// switches to the phone size after mount.
const useNotesPageSize = () => {
    const [size, setSize] = useState(NOTES_PAGE_SIZE);
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return undefined;
        // The prerender crawler uses a narrow viewport; keep its HTML at the
        // default size so it matches the first client render.
        if (navigator.userAgent === "ReactSnap") return undefined;
        const mq = window.matchMedia(PHONE_QUERY);
        const update = () => setSize(mq.matches ? NOTES_PAGE_SIZE_PHONE : NOTES_PAGE_SIZE);
        update();
        mq.addEventListener?.("change", update);
        return () => mq.removeEventListener?.("change", update);
    }, []);
    return size;
};

const usePerspectiveData = () => {
    const [page, setPage] = useState(() => peek(getPerspectivePage));
    const [posts, setPosts] = useState(() => peek(getPerspectivePosts) || []);
    const [topics, setTopics] = useState(() => peek(getPerspectiveTopics) || []);
    const [loaded, setLoaded] = useState(() => peek(getPerspectivePage) !== null);

    useEffect(() => {
        let active = true;
        Promise.all([getPerspectivePage(), getPerspectivePosts(), getPerspectiveTopics()])
            .then(([p, list, t]) => {
                if (!active) return;
                setPage(p);
                setPosts(list || []);
                setTopics(t || []);
                setLoaded(true);
            })
            .catch(() => active && setLoaded(true));
        return () => {
            active = false;
        };
    }, []);

    return { page, posts, topics, loaded };
};

const Meta = ({ children }) => <p className="text-sm text-brand-muted">{children}</p>;

const ArticleCard = ({ post }) => (
    <article data-testid="perspective-article-card" className="group flex h-full flex-col">
        <Link to={postPath(post)} className="block overflow-hidden rounded-3xl border border-brand-line bg-brand-surface2" tabIndex={-1} aria-hidden>
            {post.heroImage?.url ? (
                <img
                    src={imageUrl(post.heroImage.url, 800)}
                    alt=""
                    loading="lazy"
                    className="aspect-[3/2] w-full object-cover transition-colors duration-150"
                />
            ) : (
                <div className="aspect-[3/2] w-full el-grid-bg" />
            )}
        </Link>
        <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.24em] text-brand-accent">{post.topic?.title}</p>
        <h3 className="mt-3 font-heading text-xl font-bold leading-snug tracking-tight text-brand-text">
            <Link to={postPath(post)} className="hover:text-brand-blue">
                {post.title}
            </Link>
        </h3>
        {post.excerpt && <p className="mt-3 text-[15px] leading-relaxed text-brand-muted">{post.excerpt}</p>}
        <Meta>
            <span className="mt-4 block">{[formatDate(post.publishedAt), readingTime(post.words)].filter(Boolean).join(" · ")}</span>
        </Meta>
    </article>
);

const FeaturedArticle = ({ post }) => (
    <article data-testid="perspective-featured" className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12">
        <Link
            to={postPath(post)}
            tabIndex={-1}
            aria-hidden
            className="block overflow-hidden rounded-3xl border border-brand-line bg-brand-surface2 lg:col-span-7"
        >
            {post.heroImage?.url ? (
                <img
                    src={imageUrl(post.heroImage.url, 1400)}
                    alt=""
                    className="aspect-[16/9] w-full object-cover"
                />
            ) : (
                <div className="aspect-[16/9] w-full el-grid-bg" />
            )}
        </Link>
        <div className="lg:col-span-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-brand-accent">
                Featured · {post.topic?.title}
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold leading-[1.08] tracking-tight text-brand-text sm:text-4xl">
                <Link to={postPath(post)} className="hover:text-brand-blue">
                    {post.title}
                </Link>
            </h2>
            {post.excerpt && <p className="mt-5 text-base leading-relaxed text-brand-muted">{post.excerpt}</p>}
            <p className="mt-5 text-sm text-brand-muted">
                {[post.author?.name, formatDate(post.publishedAt), readingTime(post.words)].filter(Boolean).join(" · ")}
            </p>
            <Link
                to={postPath(post)}
                className="group mt-7 inline-flex items-center gap-2 text-sm font-semibold text-brand-text"
            >
                Read the article
                <ArrowRight className="h-4 w-4 transition-colors duration-150" aria-hidden />
            </Link>
        </div>
    </article>
);

const FieldNoteCard = ({ post, cardRef }) => (
    <article
        ref={cardRef}
        tabIndex={cardRef ? -1 : undefined}
        data-testid="perspective-field-note"
        className="flex h-full flex-col rounded-3xl border border-brand-line bg-brand-bg p-7 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
    >
        <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-brand-accent">{post.topic?.title}</p>
        <h3 className="mt-3 font-heading text-lg font-bold leading-snug tracking-tight text-brand-text">{post.title}</h3>
        <div className="mt-3 text-[15px] leading-relaxed text-brand-muted">
            <PortableText value={post.body} compact />
        </div>
        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-brand-line pt-4 text-sm">
            <span className="text-brand-muted">
                {post.originalSource?.originalUrl ? (
                    <a href={post.originalSource.originalUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-brand-text">
                        {firstSharedLine(post.originalSource)}
                    </a>
                ) : (
                    firstSharedLine(post.originalSource)
                )}
            </span>
            {hasPage(post) && (
                <Link to={postPath(post)} className="font-semibold text-brand-text hover:text-brand-blue">
                    Read the note
                </Link>
            )}
        </div>
    </article>
);

const FilterButton = ({ pressed, onClick, children }) => (
    <button
        type="button"
        aria-pressed={pressed}
        onClick={onClick}
        className={`h-10 rounded-full border px-4 text-sm transition-colors duration-150 ${
            pressed
                ? "border-brand-text bg-brand-text text-brand-bg"
                : "border-brand-line bg-brand-bg text-brand-text hover:bg-brand-surface2"
        }`}
    >
        {children}
    </button>
);

const PerspectiveContent = ({ page, posts, topics }) => {
    const [topic, setTopic] = useState("all");
    const [format, setFormat] = useState("all");
    const pageSize = useNotesPageSize();
    const [notesShown, setNotesShown] = useState(NOTES_PAGE_SIZE);
    const [lastStart, setLastStart] = useState(0);
    const firstNewNote = useRef(null);
    const focusFirstNew = useRef(false);

    // A new filter, or a change of screen size, starts the list from the top again.
    useEffect(() => {
        setNotesShown(pageSize);
        setLastStart(0);
    }, [topic, format, pageSize]);

    // After "Show more", move keyboard and screen-reader focus to the first
    // newly revealed note so the reader continues where the list grew.
    useEffect(() => {
        if (focusFirstNew.current && firstNewNote.current) {
            firstNewNote.current.focus({ preventScroll: false });
        }
        focusFirstNew.current = false;
    }, [notesShown]);

    const visibleTopics = useMemo(
        () => topics.filter((t) => posts.some((p) => p.topic?.slug === t.slug)),
        [topics, posts],
    );

    const filtered = posts.filter((p) => topic === "all" || p.topic?.slug === topic);
    const articles = filtered.filter((p) => p.format === "article");
    const notes = filtered.filter((p) => p.format === "fieldNote");
    const shownNotes = notes.slice(0, notesShown);
    const remainingNotes = notes.length - shownNotes.length;
    const previouslyShown = lastStart;

    const showMoreNotes = () => {
        focusFirstNew.current = true;
        setLastStart(shownNotes.length);
        setNotesShown((n) => n + pageSize);
    };

    const showFeatured = topic === "all" && format !== "fieldNote" && articles.length > 0;
    const featured = showFeatured ? articles.find((p) => p.featured) || articles[0] : null;
    const otherArticles = articles.filter((p) => p !== featured);

    const showArticles = format !== "fieldNote" && otherArticles.length > 0;
    const showNotes = format !== "article" && notes.length > 0;
    const nothing = !featured && !showArticles && !showNotes;

    const cta = page.closingCta;

    return (
        <>
            <section data-testid="perspective-hero" className="relative overflow-hidden pt-36 pb-12 sm:pt-44 sm:pb-16">
                <div className="pointer-events-none absolute inset-0 el-grid-bg" aria-hidden />
                <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                    <MountReveal>
                        <span className="inline-flex items-center">
                            <Overline>{page.heroLabel || "The EasiLinked Perspective"}</Overline>
                        </span>
                    </MountReveal>
                    <MountReveal delay={0.05}>
                        <h1 className="mt-6 max-w-4xl font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-6xl">
                            {page.heroHeadline}
                        </h1>
                    </MountReveal>
                    {page.heroSupportingMessage && (
                        <MountReveal delay={0.1}>
                            <p className="mt-6 max-w-2xl text-base leading-relaxed text-brand-muted sm:text-lg">
                                {page.heroSupportingMessage}
                            </p>
                        </MountReveal>
                    )}
                </div>
            </section>

            <section aria-label="Filter posts" className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="flex flex-wrap items-center justify-between gap-4 border-y border-brand-line py-5">
                    <div role="group" aria-label="Topic" className="flex flex-wrap gap-2">
                        <FilterButton pressed={topic === "all"} onClick={() => setTopic("all")}>
                            All topics
                        </FilterButton>
                        {visibleTopics.map((t) => (
                            <FilterButton key={t._id} pressed={topic === t.slug} onClick={() => setTopic(t.slug)}>
                                {t.title}
                            </FilterButton>
                        ))}
                    </div>
                    <div role="group" aria-label="Format" className="flex gap-1 rounded-xl bg-brand-surface2 p-1">
                        {FORMATS.map((f) => (
                            <button
                                key={f.value}
                                type="button"
                                aria-pressed={format === f.value}
                                onClick={() => setFormat(f.value)}
                                className={`h-9 rounded-lg px-4 text-sm transition-colors duration-150 ${
                                    format === f.value
                                        ? "bg-brand-bg font-semibold text-brand-text shadow-sm"
                                        : "text-brand-muted hover:text-brand-text"
                                }`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <div aria-live="polite">
                {featured && (
                    <section className="mx-auto max-w-7xl px-5 pt-14 sm:px-8">
                        <FeaturedArticle post={featured} />
                    </section>
                )}

                {showArticles && (
                    <section aria-labelledby="perspective-articles" className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
                        <h2 id="perspective-articles" className="scroll-mt-28 font-heading text-2xl font-bold tracking-tight text-brand-text sm:text-3xl">
                            {page.articlesHeadline || "Latest articles"}
                        </h2>
                        <div className="mt-8 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3">
                            {otherArticles.map((p) => (
                                <ArticleCard key={p._id} post={p} />
                            ))}
                        </div>
                    </section>
                )}

                {showNotes && (
                    <section
                        aria-labelledby="perspective-notes"
                        data-testid="perspective-field-notes"
                        className="mt-12 border-y border-brand-line bg-brand-surface py-20"
                    >
                        <div className="mx-auto max-w-7xl px-5 sm:px-8">
                            <Overline>Field Notes</Overline>
                            {page.fieldNotesHeadline && (
                                <h2 id="perspective-notes" className="scroll-mt-28 mt-4 font-heading text-2xl font-bold tracking-tight text-brand-text sm:text-3xl">
                                    {page.fieldNotesHeadline}
                                </h2>
                            )}
                            {page.fieldNotesIntro && (
                                <p className="mt-3 max-w-2xl text-base leading-relaxed text-brand-muted">{page.fieldNotesIntro}</p>
                            )}
                            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                                {shownNotes.map((p, i) => (
                                    <FieldNoteCard
                                        key={p._id}
                                        post={p}
                                        cardRef={previouslyShown > 0 && i === previouslyShown ? firstNewNote : undefined}
                                    />
                                ))}
                            </div>
                            {notes.length > pageSize && (
                                <div className="mt-10 flex flex-col items-center gap-4">
                                    <p className="text-sm text-brand-muted" data-testid="perspective-notes-count">
                                        Showing {shownNotes.length} of {notes.length} Field Notes
                                    </p>
                                    {remainingNotes > 0 && (
                                        <button
                                            type="button"
                                            onClick={showMoreNotes}
                                            data-testid="perspective-notes-more"
                                            className="h-11 rounded-control border-[1.5px] border-brand-control-border bg-brand-bg px-6 text-sm font-semibold text-brand-text transition-colors duration-150 hover:bg-brand-surface2"
                                        >
                                            Show more Field Notes
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {nothing && (
                    <section className="mx-auto max-w-7xl px-5 py-20 text-center sm:px-8">
                        <p className="text-base text-brand-muted">Nothing here yet for this filter. Try another topic or format.</p>
                    </section>
                )}
            </div>

            {(page.closingHeadline || cta?.url) && (
                <section data-testid="perspective-closing-cta" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-28">
                    <div className="relative overflow-hidden rounded-card border border-brand-line bg-brand-surface px-6 py-16 text-center sm:px-16 sm:py-20">
                        <div className="pointer-events-none absolute inset-0 el-grid-bg opacity-40" aria-hidden />
                        {page.closingHeadline && (
                            <h2 className="relative mx-auto max-w-3xl font-heading text-3xl font-extrabold leading-[1.05] tracking-tight text-brand-text sm:text-5xl">
                                {page.closingHeadline}
                            </h2>
                        )}
                        {page.closingSupportingMessage && (
                            <p className="relative mx-auto mt-5 max-w-xl text-base leading-relaxed text-brand-muted">
                                {page.closingSupportingMessage}
                            </p>
                        )}
                        {cta?.url && (
                            <div className="relative mt-9 flex justify-center">
                                <CtaLink
                                    href={cta.url}
                                    label={cta.label || "Schedule a Conversation"}
                                    openInNewTab={cta.openInNewTab}
                                    testid="perspective-closing-cta-button"
                                />
                            </div>
                        )}
                    </div>
                </section>
            )}
        </>
    );
};

const PerspectiveRoute = () => {
    const { page, posts, topics, loaded } = usePerspectiveData();
    useSeo(isLive(page) ? page.seo : undefined);

    if (!isLive(page)) {
        if (!loaded) return <PageLayout><section className="min-h-[60vh]" /></PageLayout>;
        return <NotFoundPage />;
    }
    return (
        <PageLayout>
            <PerspectiveContent page={page} posts={posts} topics={topics} />
        </PageLayout>
    );
};

export default PerspectiveRoute;
